import { ref } from 'vue';

/**
 * Composable for managing the state of the multi-level mobile menu.
 */
export function useMobileMenu() {
  const isMobileOpen = ref(false);
  const menuStates = ref({}); // L1: { 'menu-id': boolean }
  const submenuStates = ref({}); // L2: { 'submenu-id': boolean }
  const accordionMenuStates = ref({}); // L3: { 'accordion-id': boolean }

  const toggleMobileMenuState = () => {
    isMobileOpen.value = !isMobileOpen.value;

    if (!isMobileOpen.value) {
      resetAllMenuStates();
    }
  };

  /**
   * Toggles the Level 1 menu state (used for L0 <-> L1 navigation).
   * @param {string} menuId - The ID of the menu panel being toggled.
   * @returns {boolean} - True if the menu was opened, false if closed.
   */
  const toggleMenuState = (menuId) => {
    const isCurrentlyOpen = menuStates.value[menuId] === true;
    const willOpen = !isCurrentlyOpen;

    submenuStates.value = {};
    accordionMenuStates.value = {};

    menuStates.value = { [menuId]: willOpen };

    return willOpen;
  };

  /**
   * Explicitly sets a Level 1 menu state to active (true).
   * Used when navigating back from L2 to L1.
   * @param {string} menuId - The ID of the menu panel to activate.
   */
  const setMenuStateActive = (menuId) => {
    submenuStates.value = {};
    accordionMenuStates.value = {};
    menuStates.value = { [menuId]: true };
  };

  /**
   * Updates the Level 2 submenu state.
   * Assumes this is called when opening an L2 menu.
   * @param {string} submenuId - The ID of the submenu panel being interacted with.
   */
  const toggleSubMenuState = (submenuId) => {
    accordionMenuStates.value = {};
    submenuStates.value = { [submenuId]: true };
  };

  /**
   * Updates the Level 3 accordion state. Allows multiple accordions to be open/closed independently.
   * @param {string} accordionId - The ID of the accordion being toggled.
   */
  const toggleAccordionState = (accordionId) => {
    const current = accordionMenuStates.value[accordionId] ?? false;
    const willOpen = !current;

    accordionMenuStates.value[accordionId] = willOpen;
  };

  /** Resets all menu level states. */
  const resetAllMenuStates = () => {
    menuStates.value = {};
    submenuStates.value = {};
    accordionMenuStates.value = {};
  };

  /**
   * Sets accordion menus to an open state (true) for a specific panel.
   * This REPLACES the existing accordion state, assuming navigation has just cleared it.
   * @param {Array<string>} [accordionIds=[]] - List of accordion IDs to open by default *for the current panel*.
   */
  const setAccordionsOpenByDefault = (accordionIds = []) => {
    const newAccordionStates = {};
    if (accordionIds.length) {
      accordionIds.forEach((id) => {
        newAccordionStates[id] = true;
      });
    }

    accordionMenuStates.value = newAccordionStates;
  };

  // --- State Checkers ---
  const isMenuOpen = (menuId) => menuStates.value[menuId] === true;
  const isSubMenuOpen = (menuId) => submenuStates.value[menuId] === true;
  const isAccordionOpen = (menuId) => accordionMenuStates.value[menuId] === true;
  const isAnyMenuActive = () => Object.values(menuStates.value).some((isOpen) => isOpen === true);
  const isAnySubMenuActive = () =>
    Object.values(submenuStates.value).some((isOpen) => isOpen === true);

  return {
    accordionMenuStates,
    isAccordionOpen,
    isAnyMenuActive,
    isAnySubMenuActive,
    isMenuOpen,
    isMobileOpen,
    isSubMenuOpen,
    menuStates,
    resetAllMenuStates,
    setAccordionsOpenByDefault,
    setMenuStateActive,
    submenuStates,
    toggleAccordionState,
    toggleMenuState,
    toggleMobileMenuState,
    toggleSubMenuState,
  };
}
